import {
	PrepareFilesResponseType,
	PrepareFilesType,
	ValidateFilesResponseType,
	ValidateFilesType,
} from './UploadFileTypes';
import { ColourMod } from '../../../Data/Enums/LayoutEnums';
import { GetReadableFileSize } from '../../../Constants/Helpers';

const ValidateFiles = (props: ValidateFilesType): ValidateFilesResponseType => {
	let fileError: ValidateFilesResponseType = {
		fileNames: [],
		title: '',
		message: '',
		state: false,
		colour: ColourMod.Alert,
	};

	const invalidFileNameList: string[] = [];
	const fileValidation: any = {};

	if (!props.files || props.files.length === 0) {
		props.onChangeCallback(undefined);
		return fileError;
	}

	const fileCollection = props.singleFile ? [Array.from(props.files)[0]] : Array.from(props.files);

	const fileTypesValid = [true];
	const fileSizesValid = [true];

	let totalFileSize = 0;

	if (props.singleFile && props.files.length > 1) {
		fileError = {
			fileNames: [],
			state: true,
			colour: ColourMod.Warning,
			title: 'Multiple Files Detected',
			message: 'Only the first file will be used',
		};
	}

	if (props.files.length > props.maxFiles) {
		fileError = {
			fileNames: [],
			state: true,
			colour: ColourMod.Warning,
			title: `Too Many Files Selected (${props.files.length}/${props.maxFiles})`,
			message: `Please remove ${
				props.files.length - props.maxFiles === 1 ? 'a file' : `${props.files.length - props.maxFiles} files`
			} before continuing`,
		};
	}

	fileCollection.forEach((file: any) => {
		totalFileSize += file.size;

		const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

		if (!props.acceptsMimeType && props.acceptsFileExtension && props.acceptsFileExtension.length > 0) {
			if (!props.acceptsFileExtension.includes(fileExtension)) {
				invalidFileNameList.push(file.name);
				fileTypesValid.push(false);
			}
		}

		if (props.acceptsMimeType && props.acceptsMimeType.length > 0) {
			props.acceptsMimeType.forEach((mimeType) => {
				if (!fileValidation[file.name]) {
					fileValidation[file.name] =
						new RegExp(mimeType).test(file.type) ||
						(props.acceptsFileExtension !== undefined &&
							props.acceptsFileExtension.includes(fileExtension));
				}
			});

			if (Object.values(fileValidation).includes(false)) {
				fileTypesValid.push(false);
				Object.keys(fileValidation).forEach((key) => {
					if (!fileValidation[key]) {
						invalidFileNameList.push(key);
					}
				});
			}
		}

		if (file.size > props.maxSize) {
			fileSizesValid.push(false);
		}
	});

	if (!props.mergeFiles && fileTypesValid.every((v) => v) && fileSizesValid.every((v) => v)) {
		props.onChangeCallback(fileCollection);
	} else {
		props.onChangeCallback(undefined);

		if ((props.acceptsMimeType || props.acceptsFileExtension) && !fileTypesValid.every((v) => v)) {
			const errorFileExtHints = props.acceptsFileExtension
				? props.acceptsFileExtension.toString().replace(/,/g, ', ')
				: '';
			const errorFileMimeHints = props.acceptsMimeType
				? props.acceptsMimeType.toString().replace(/,/g, ', ')
				: '';

			const errorMessage = `Please select files that match the following types: ${
				errorFileMimeHints ? `${errorFileMimeHints}, ${errorFileExtHints}` : errorFileExtHints
			}`;

			fileError = {
				fileNames: Array.from(invalidFileNameList),
				state: true,
				colour: ColourMod.Alert,
				title: 'Selection Includes an Invalid File',
				message: errorMessage,
			};
		}

		if (props.mergeFiles && totalFileSize > props.maxSize) {
			fileError = {
				fileNames: [],
				state: true,
				colour: ColourMod.Alert,
				title: `Selected ${props.files.length === 1 ? 'File is' : 'Files are'} Too Large (${GetReadableFileSize(
					totalFileSize
				)})`,
				message: `Please select ${
					props.files.length === 1 ? 'a file that is' : 'files that are'
				} under ${GetReadableFileSize(props.maxSize)}.`,
			};
		}

		if (!props.mergeFiles && !fileSizesValid.every((v) => v)) {
			fileError = {
				fileNames: [],
				state: true,
				colour: ColourMod.Alert,
				title: `Selected ${props.files.length === 1 ? 'File is' : 'Files are'} Too Large (${GetReadableFileSize(
					totalFileSize
				)})`,
				message: `Please select ${
					props.files.length === 1 ? 'a file that is' : 'files that are'
				} under ${GetReadableFileSize(props.maxSize)}.`,
			};
		}
	}

	return fileError;
};

const PrepareFiles = (props: PrepareFilesType, skipDeDupe = false): PrepareFilesResponseType => {
	const allAttachments = props.singleFile
		? [Array.from(props.selectedFiles)[0]] || []
		: [...props.existingFiles, ...Array.from(props.selectedFiles)];

	const uniques = new Set();
	const newFilesArray = skipDeDupe
		? [...allAttachments]
		: allAttachments.filter((file) => {
				const fileKey = `${file.name}${file.size}${file.type}${file.lastModified}`;
				return !uniques.has(fileKey) && uniques.add(fileKey);
		  });

	const validationResponse = ValidateFiles({
		files: newFilesArray,
		maxSize: props.maxSize,
		maxFiles: props.maxFiles,
		acceptsFileExtension: props.acceptsFileExtension,
		acceptsMimeType: props.acceptsMimeType,
		singleFile: props.singleFile,
		mergeFiles: props.mergeFiles,
		onChangeCallback: props.onChangeCallback,
	});

	newFilesArray.forEach((entry: any, index, object) => {
		entry.invalid = validationResponse.fileNames.includes(entry.name);
	});

	return { files: newFilesArray, errors: validationResponse };
};

export { ValidateFiles, PrepareFiles };
